import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { User, Lock, ScanBarcode, Globe } from 'lucide-react';
import { LogoBrain } from '../../components/Logo';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POSLogin = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    pin: ''
  });

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
    localStorage.setItem('i18nextLng', newLang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await axios.post(`${API_URL}/api/pos/auth/login`, formData);
      localStorage.setItem('pos_token', res.data.token);
      localStorage.setItem('pos_user', JSON.stringify(res.data.user));
      toast.success(isRTL ? `مرحباً، ${res.data.user.name}!` : `Welcome, ${res.data.user.name}!`);
      navigate('/pos-terminal');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isRTL ? 'بيانات غير صحيحة' : 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  // Quick PIN buttons
  const handlePinInput = (digit) => {
    if (formData.pin.length < 6) {
      setFormData(prev => ({ ...prev, pin: prev.pin + digit }));
    }
  };

  const handlePinClear = () => {
    setFormData(prev => ({ ...prev, pin: '' }));
  };

  const handlePinBackspace = () => {
    setFormData(prev => ({ ...prev, pin: prev.pin.slice(0, -1) }));
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4 ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:40px_40px]" />
      
      {/* Language Toggle */}
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleLanguage}
        className="absolute top-4 right-4 text-slate-400 hover:text-white z-20"
      >
        <Globe className="h-4 w-4 mr-1" />
        {i18n.language === 'ar' ? 'EN' : 'AR'}
      </Button>
      
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <LogoBrain size={72} animate={true} />
          </div>
          <h1 className="text-3xl font-bold text-white">{t('pos.login.title')}</h1>
          <p className="text-slate-400 mt-1">{t('pos.login.subtitle')}</p>
        </div>

        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-white text-xl">{t('pos.login.cashierLogin')}</CardTitle>
            <CardDescription className="text-slate-400">
              {t('pos.login.enterCredentials')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Username */}
              <div className="space-y-2">
                <Label className={`text-slate-300 ${isRTL ? 'block text-right' : ''}`}>{t('pos.login.username')}</Label>
                <div className="relative">
                  <User className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500`} />
                  <Input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder={t('pos.login.usernamePlaceholder')}
                    className={`${isRTL ? 'pr-10 text-right' : 'pl-10'} bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500`}
                    required
                  />
                </div>
              </div>

              {/* PIN Display */}
              <div className="space-y-2">
                <Label className={`text-slate-300 ${isRTL ? 'block text-right' : ''}`}>{t('pos.login.pinCode')}</Label>
                <div className="relative">
                  <Lock className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500`} />
                  <Input
                    type="password"
                    value={formData.pin}
                    readOnly
                    placeholder={t('pos.login.pinPlaceholder')}
                    className={`${isRTL ? 'pr-10' : 'pl-10'} bg-slate-900/50 border-slate-600 text-white text-center text-2xl tracking-[0.5em] placeholder:text-slate-500 placeholder:text-sm placeholder:tracking-normal`}
                  />
                </div>
              </div>

              {/* PIN Pad */}
              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                  <Button
                    key={digit}
                    type="button"
                    variant="outline"
                    className="h-14 text-xl font-bold bg-slate-900/50 border-slate-600 text-white hover:bg-slate-700 hover:text-white"
                    onClick={() => handlePinInput(digit.toString())}
                  >
                    {digit}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  className="h-14 text-sm bg-slate-900/50 border-slate-600 text-red-400 hover:bg-red-900/30 hover:text-red-300"
                  onClick={handlePinClear}
                >
                  {t('pos.login.clear')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-14 text-xl font-bold bg-slate-900/50 border-slate-600 text-white hover:bg-slate-700 hover:text-white"
                  onClick={() => handlePinInput('0')}
                >
                  0
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-14 text-sm bg-slate-900/50 border-slate-600 text-amber-400 hover:bg-amber-900/30 hover:text-amber-300"
                  onClick={handlePinBackspace}
                >
                  ⌫
                </Button>
              </div>

              {/* Login Button */}
              <Button
                type="submit"
                className="w-full h-12 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                disabled={loading || formData.pin.length < 4}
              >
                {loading ? t('pos.login.signingIn') : t('pos.login.startShift')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-500 text-sm">
                {t('pos.login.forgotPin')}
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          {t('pos.login.copyright')}
        </p>
      </div>
    </div>
  );
};

export default POSLogin;
